import { Box } from "@mui/joy";
import { parse } from "mathjs";
import type { ReactNode } from "react";

type ParsedNode = {
    type?: string;
    mathjs?: string;
    op?: string;
    fn?: ParsedNode | string;
    name?: string;
    value?: number | string | boolean;
    args?: ParsedNode[];
    content?: ParsedNode;
    condition?: ParsedNode;
    trueExpr?: ParsedNode;
    falseExpr?: ParsedNode;
};

const OPERATOR_SYMBOL: Record<string, string> = {
    "+": "+",
    "-": "−",
    "*": "×",
    "/": "÷",
    ">": ">",
    "<": "<",
    ">=": "≥",
    "<=": "≤",
    "==": "=",
    "!=": "≠",
};

function getNodeKind(node: ParsedNode): string | undefined {
    return node.type ?? node.mathjs;
}

function renderNode(node: ParsedNode, key: string): ReactNode {
    switch (getNodeKind(node)) {
        case "ParenthesisNode":
            return renderNode(node.content!, key);
        case "SymbolNode":
            return (
                <Box
                    component="span"
                    key={key}
                    sx={{ fontWeight: 600, color: "primary.700" }}
                >
                    {node.name}
                </Box>
            );
        case "ConstantNode":
            return (
                <Box component="span" key={key} sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatConstant(node.value)}
                </Box>
            );
        case "FunctionNode":
            return renderFunction(node, key);
        case "ConditionalNode":
            return (
                <Box
                    component="span"
                    key={key}
                    sx={{
                        display: "inline-flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 0.5,
                    }}
                >
                    {renderNode(node.condition!, `${key}-cond`)}
                    <Box component="span" sx={{ color: "neutral.500", px: 0.25 }}>
                        ?
                    </Box>
                    {renderNode(node.trueExpr!, `${key}-true`)}
                    <Box component="span" sx={{ color: "neutral.500", px: 0.25 }}>
                        :
                    </Box>
                    {renderNode(node.falseExpr!, `${key}-false`)}
                </Box>
            );
        case "OperatorNode":
            return renderOperator(node, key);
        default:
            return (
                <Box component="span" key={key}>
                    {String(node.value ?? node.name ?? "")}
                </Box>
            );
    }
}

function renderFunction(node: ParsedNode, key: string) {
    const fnName =
        typeof node.fn === "string" ? node.fn : node.fn?.name ?? "f";
    const arg = node.args?.[0];

    if (fnName === "ceil" && arg) {
        return (
            <Box component="span" key={key} sx={{ display: "inline-flex", alignItems: "center" }}>
                <Box component="span" sx={{ fontSize: "1.15em", lineHeight: 1, mr: 0.1 }}>
                    ⌈
                </Box>
                {renderNode(arg, `${key}-arg`)}
                <Box component="span" sx={{ fontSize: "1.15em", lineHeight: 1, ml: 0.1 }}>
                    ⌉
                </Box>
            </Box>
        );
    }

    if (fnName === "floor" && arg) {
        return (
            <Box component="span" key={key} sx={{ display: "inline-flex", alignItems: "center" }}>
                <Box component="span" sx={{ fontSize: "1.15em", lineHeight: 1, mr: 0.1 }}>
                    ⌊
                </Box>
                {renderNode(arg, `${key}-arg`)}
                <Box component="span" sx={{ fontSize: "1.15em", lineHeight: 1, ml: 0.1 }}>
                    ⌋
                </Box>
            </Box>
        );
    }

    return (
        <Box component="span" key={key} sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
            <Box component="span" sx={{ color: "neutral.500", fontSize: "0.9em" }}>
                {fnName}
            </Box>
            <Box component="span">(</Box>
            {node.args?.map((child, index) => (
                <Box
                    component="span"
                    key={`${key}-arg-${index}`}
                    sx={{ display: "inline-flex", alignItems: "center" }}
                >
                    {index > 0 && (
                        <Box component="span" sx={{ color: "neutral.500", px: 0.25 }}>
                            ,
                        </Box>
                    )}
                    {renderNode(child, `${key}-arg-${index}`)}
                </Box>
            ))}
            <Box component="span">)</Box>
        </Box>
    );
}

function renderOperator(node: ParsedNode, key: string) {
    const [left, right] = node.args ?? [];

    if (node.op === "/" && left && right) {
        return (
            <Box
                component="span"
                key={key}
                sx={{
                    display: "inline-flex",
                    flexDirection: "column",
                    alignItems: "center",
                    verticalAlign: "middle",
                    mx: 0.5,
                    my: 0.25,
                    lineHeight: 1.3,
                }}
            >
                <Box
                    sx={{
                        px: 0.75,
                        pb: 0.35,
                        borderBottom: "1px solid",
                        borderColor: "neutral.500",
                        textAlign: "center",
                    }}
                >
                    {renderNode(left, `${key}-num`)}
                </Box>
                <Box sx={{ px: 0.75, pt: 0.35, textAlign: "center" }}>
                    {renderNode(right, `${key}-den`)}
                </Box>
            </Box>
        );
    }

    const symbol = OPERATOR_SYMBOL[node.op ?? ""] ?? node.op;

    if (node.op === "-" && node.args?.length === 1) {
        return (
            <Box component="span" key={key} sx={{ display: "inline-flex", alignItems: "center" }}>
                <Box component="span" sx={{ color: "neutral.500", mr: 0.25 }}>
                    −
                </Box>
                {renderNode(node.args[0], `${key}-arg`)}
            </Box>
        );
    }

    return (
        <Box
            component="span"
            key={key}
            sx={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}
        >
            {left && renderNode(left, `${key}-left`)}
            <Box component="span" sx={{ color: "neutral.500", px: 0.25 }}>
                {symbol}
            </Box>
            {right && renderNode(right, `${key}-right`)}
        </Box>
    );
}

const FORMULA_HANDWRITTEN_FONT = '"Handlee", "Segoe Print", "Apple Chancery", cursive';

export const formulaHandwritingSx = {
    fontFamily: FORMULA_HANDWRITTEN_FONT,
    fontSize: "0.8125rem",
    lineHeight: 1.4,
} as const;

export const formulaSectionHeadingSx = {
    fontFamily: 'var(--std-font-family, "Outfit", sans-serif)',
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "neutral.600",
} as const;

function formatConstant(value: number | string | boolean | undefined): string {
    if (value === undefined || value === null) return "";
    if (typeof value === "boolean") return value ? "true" : "false";
    const num = Number(value);
    if (!Number.isNaN(num) && Number.isFinite(num)) {
        return Number.isInteger(num) ? String(num) : String(Math.round(num * 100) / 100);
    }
    return String(value);
}

export function FormulaExpression({ expression }: { expression: string }) {
    if (!expression?.trim()) return null;

    try {
        const node = parse(expression) as ParsedNode;
        return (
            <Box
                sx={{
                    ...formulaHandwritingSx,
                    fontVariantNumeric: "tabular-nums",
                    color: "text.primary",
                    py: 0.25,
                }}
            >
                {renderNode(node, "root")}
            </Box>
        );
    } catch {
        return (
            <Box
                sx={{
                    ...formulaHandwritingSx,
                    wordBreak: "break-word",
                    py: 0.25,
                }}
            >
                {expression}
            </Box>
        );
    }
}
